import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { ReactState } from '../../../../../../React/Common/Types/Types'
import Tabs from '../../../../../../React/Common/Components/Tabs'
import { UserInfo, useUserInfo } from '../../../../../Hooks/useUserInfo'
import Loader from '../../../../../../React/Common/Components/Loader'
import { FormProvider, useForm, UseFormMethods } from '../../../../../../Vendor/react-hook-form/dist/index.ie11'
import { ON_CHANGE } from '../../../../../../React/Common/Constants/HookFormEventNames'
import { ClientConfigurationModalID } from './ClientConfigurationModalID'
import { Tab } from '../../../../../../React/Common/Components/Tabs/Types'
import CLanguageHandler from '../../../../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../../../../../React/Common/Contexts/LanguageContext'
import { ClientConfigurationModalLanguage } from '../../../../../../../i18n'
import { IconName } from '../../../../../../React/Common/Components/Icon'
import { ClientConfigurationBackend, ClientConfigurationForm, SelectedEntry } from '../Types/Types'
import { EMPTY_STRING } from '../../../../../../NonReact/constants/Common'
import { DEFAULT_CME_ID } from '../../../../../../React/Common/Constants/ServerValues'
import {
    ALL_VALUE,
    ANY_DEVICE_VALUE,
    ANY_VALUE_STRING_DEFAULT
} from '../../../../../../React/Common/Constants/DefaultValues'
import { ClientConfigurationDetails } from './ClientConfigurationDetails'
import { isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { ClientConfigurationPolicyTab } from './ClientConfigurationPolicyTab'
import { ClientConfigurationSettingsTab } from './ClientConfigurationSettingsTab'
import { mapToServer } from '../Mappers/ClientConfigurationFormToServerMapper'
import { RoundedButton } from '../../../../Atoms/RoundedButton/RoundedButton'
import { getClientConfiguration } from '../../../../../../React/Common/API/ItemGetters'
import { displayError, displayErrorMessage } from '../../../../../../React/Common/Utils/Messages'
import { mapFromServer } from '../Mappers/ClientConfigurationServerToFormMapper'
import { RESTAPIError } from '../../../../../../React/Common/API'
import { StatusCodes } from '../../../../../../NonReact/constants/StatusCodes'
import {
    addClientConfiguration,
    changeClientConfiguration
} from '../../../../../../NonReact/common/API/EntityAPIClient'
import { APIData } from '../../../../../../NonReact/common/Types/Types'
import { GenericScopeAssignmentTab } from '../../../../Molecules/GenericScopeTab/GenericScopeTab'
import {
    CME_ID_FIELD_NAME,
    DEVICE_ID_FIELD_NAME,
    FILTER_RECURSIVELY_FIELD_NAME,
    PARTITION_ID_FIELD_NAME,
    TELEPHONY_SERVER_ID_FIELD_NAME
} from '../Constants/FieldNames'
import { AdminFilterSingleValues } from '../../../../Molecules/AdminFilters/AdminFilters'

type Props = {
    selectedEntry?: SelectedEntry
    isViewOnly?: boolean
    closeModal: (refresh?: boolean) => void
    filters: Nullable<AdminFilterSingleValues>
}

export const ClientConfigurationTabs: FC<Props> = ({
    selectedEntry,
    isViewOnly,
    closeModal,
    filters
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [userInfo]: UserInfo = useUserInfo()
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(false)
    const [activeTabID, setActiveTabID]: ReactState<ClientConfigurationModalID> = useState<ClientConfigurationModalID>(
        ClientConfigurationModalID.DETAILS
    )

    const defaultValues: MutableRefObject<ClientConfigurationForm> = useRef<ClientConfigurationForm>({
        details: {
            id: EMPTY_STRING,
            description: EMPTY_STRING
        },
        scope: {
            cmeID: filters?.cme?.toString() ?? DEFAULT_CME_ID,
            telsysID: Number(filters?.telephonyServer) ?? ALL_VALUE,
            partitionID: Number(filters?.partition) ?? ALL_VALUE,
            deviceID: ANY_VALUE_STRING_DEFAULT,
            filterRecursively: true
        },
        policy: {
            item: EMPTY_STRING,
            value: false,
            policies: []
        },
        settings: {
            item: EMPTY_STRING,
            value: false,
            settings: []
        }
    })

    // Form context.
    const methods: UseFormMethods<ClientConfigurationForm> = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { handleSubmit, reset, errors }: UseFormMethods<ClientConfigurationForm> = methods

    const validationErrors: boolean[] = useMemo(
        (): boolean[] => [!!errors?.details, !!errors?.scope, !!errors?.policy, !!errors?.settings],
        [errors]
    )

    const tabs: Tab[] = [
        {
            ID: ClientConfigurationModalID.DETAILS,
            isDisplayed: true,
            content: <ClientConfigurationDetails isViewOnly={isViewOnly} isEditMode={isVarNotEmpty(selectedEntry)} />,
            hasError: validationErrors[ClientConfigurationModalID.DETAILS],
            tabsMenuItem: {
                title: language.get(ClientConfigurationModalLanguage.ClientConfigurationModal_Tabs_Details),
                iconName: IconName.PERSON,
                onClick: (): void => setActiveTabID(ClientConfigurationModalID.DETAILS)
            }
        },
        {
            ID: ClientConfigurationModalID.SCOPE,
            isDisplayed: true,
            content: (
                <GenericScopeAssignmentTab
                    fieldNames={{
                        cmeId: CME_ID_FIELD_NAME,
                        telSysId: TELEPHONY_SERVER_ID_FIELD_NAME,
                        partitionId: PARTITION_ID_FIELD_NAME,
                        deviceId: DEVICE_ID_FIELD_NAME
                    }}
                    isViewOnly={isViewOnly}
                />
            ),
            hasError: validationErrors[ClientConfigurationModalID.SCOPE],
            tabsMenuItem: {
                title: language.get(ClientConfigurationModalLanguage.ClientConfigurationModal_Tabs_Scope),
                iconName: IconName.LOCKED,
                onClick: (): void => setActiveTabID(ClientConfigurationModalID.SCOPE)
            }
        },
        {
            ID: ClientConfigurationModalID.POLICY,
            isDisplayed: true,
            content: <ClientConfigurationPolicyTab isViewOnly={isViewOnly} />,
            hasError: validationErrors[ClientConfigurationModalID.POLICY],
            tabsMenuItem: {
                title: language.get(ClientConfigurationModalLanguage.ClientConfigurationModal_Tabs_Policy),
                iconName: IconName.POLICY,
                onClick: (): void => setActiveTabID(ClientConfigurationModalID.POLICY)
            }
        },
        {
            ID: ClientConfigurationModalID.SETTINGS,
            isDisplayed: true,
            content: <ClientConfigurationSettingsTab isViewOnly={isViewOnly} />,
            hasError: validationErrors[ClientConfigurationModalID.SETTINGS],
            tabsMenuItem: {
                title: language.get(ClientConfigurationModalLanguage.ClientConfigurationModal_Tabs_Settings),
                iconName: IconName.SETTINGS_CONFIG,
                onClick: (): void => setActiveTabID(ClientConfigurationModalID.SETTINGS)
            }
        }
    ]

    const successfulSubmit = (formData: ClientConfigurationForm): void => {
        const { telsysID, partitionID, deviceID }: ClientConfigurationForm['scope'] = formData.scope
        const device: string = deviceID === ANY_VALUE_STRING_DEFAULT ? ANY_DEVICE_VALUE : deviceID

        if (isVarNotEmpty(selectedEntry)) {
            const { id }: SelectedEntry = selectedEntry

            setIsLoading(true)

            changeClientConfiguration(telsysID, partitionID, device, id, mapToServer(formData))
                .then((): void => closeModal(true))
                .catch(displayError)
                .finally((): void => {
                    if (isMounted.current) {
                        setIsLoading(false)
                    }
                })
        } else {
            setIsLoading(true)

            addClientConfiguration(telsysID, partitionID, device, mapToServer(formData))
                .then((): void => closeModal(true))
                .catch(displayError)
                .finally((): void => {
                    if (isMounted.current) {
                        setIsLoading(false)
                    }
                })
        }
    }

    const invalidSubmit = (): void =>
        setActiveTabID(validationErrors.findIndex((validationError: boolean): boolean => validationError))

    useEffect((): void => {
        if (isVarNotEmpty(selectedEntry)) {
            setIsLoading(true)

            const { telsysID, partitionID, deviceID, id }: SelectedEntry = selectedEntry

            getClientConfiguration(telsysID, partitionID, deviceID, id)
                .then((data: Nullable<APIData<ClientConfigurationBackend>>): void => {
                    if (isVarNotEmpty(data?.Data)) {
                        const clientConfigurationForm: ClientConfigurationForm = mapFromServer(data.Data, selectedEntry)

                        reset(clientConfigurationForm)

                        setTimeout((): void => reset(clientConfigurationForm))
                    }
                })
                .catch((e: RESTAPIError): void => {
                    if (e.HTTPStatus === StatusCodes.NOT_FOUND) {
                        displayErrorMessage(
                            language.get(ClientConfigurationModalLanguage.ClientConfigurationModal_Error_NotFound)
                        )
                    } else {
                        displayError(e)
                    }
                })
                .finally((): void => {
                    if (isMounted.current) {
                        setIsLoading(false)
                    }
                })
        }
    }, [])

    return (
        <div className='config-tabs'>
            {isLoading || !userInfo ? (
                <Loader />
            ) : (
                <FormProvider {...methods}>
                    <form
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onSubmit={handleSubmit(successfulSubmit, invalidSubmit)}
                        noValidate
                    >
                        {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                        {!isViewOnly ? (
                            <div className='footer-actions'>
                                <RoundedButton type='button' isOutlined onClick={(): void => closeModal()} shrink>
                                    {language.get(
                                        ClientConfigurationModalLanguage.ClientConfigurationModal_Footer_Actions_Cancel
                                    )}
                                </RoundedButton>
                                <RoundedButton type='submit' shrink>
                                    {language.get(
                                        ClientConfigurationModalLanguage.ClientConfigurationModal_Footer_Actions_Submit
                                    )}
                                </RoundedButton>
                            </div>
                        ) : (
                            <></>
                        )}
                    </form>
                </FormProvider>
            )}
        </div>
    )
}
